import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  ListItem,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { capitalize, isEmpty, startCase } from 'lodash';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { i18n, useTranslation } from 'next-i18next';
import TurndownService from 'turndown';
import CMCCloudBreadcrumbs from '@/components/breadcrums';
import DocumentHomePage from '@/components/document-home';
import MenuDetailDocument from '@/components/menu-detail-document';
import NoContent from '@/components/no-content';
import Page from '@/components/page';
import { DATE_TIME_FORMAT } from '@/constants';
import { useThemeContext } from '@/context/theme-provider';
import { getDocumentHomePages, getPageDocument } from '@/services/apis';
import { scrollToTop } from '@/utils';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useResponsive from '@/hooks/useResponsive';
import { toast } from 'react-toastify';
import Link from 'next/link';
import CloseIcon from '@mui/icons-material/Close';
import { LayoutContext } from '@/layouts';

const turndownService = new TurndownService();

const COPY_ACTIONS = {
  VIEW_AS_MD: 'view_as_md',
  COPY_AS_MD: 'copy_as_md',
  OPEN_IN_CHATGPT: 'open_in_chatgpt',
  OPEN_IN_CLAUDE: 'open_in_claude',
};

export default function DocumentReuseable({
  initDocument = [],
  initDocumentHomePage = [],
  initProductData = [],
}) {
  const router = useRouter();
  const { productId = [] } = router.query;
  const [highlightTitle, setHighLightTitle] = useState([]);
  const [readingMinutes, setReadingMinutes] = useState(1);
  const [isTocOpen, setIsTocOpen] = useState(true);
  const currentContent = useRef(null);
  const { t } = useTranslation(['messages', 'news', 'document']);
  const { sidebarData } = useThemeContext();
  const [documentHomePage, setDocumentHomePageData] = useState(initDocumentHomePage);
  const [documentData, setDocumentData] = useState(initDocument);
  const copyBtnRef = useRef(null);
  const [copyVisible, setCopyVisible] = useState(false);
  const [copying, setCopying] = useState(false);
  const theme = useTheme();
  const isDesktop = useResponsive('up', 'md');
  const [stickyMode, setStickyMode] = useState(false);
  const tocRef = useRef(null);
  const right = useRef(null);
  const [zoomingImageSrc, setImageSrc] = useState(null);

  const isDocumentHomePage = useMemo(() => productId?.length === 2, [productId]);
  const isProductIntroPage = useMemo(() => productId?.length === 1, [productId]);
  const isProductDetailPage = useMemo(() => productId?.length === 3, [productId]);

  const productName = startCase(router?.query?.productId?.[0]?.replaceAll('-', ' '));
  const { isDocumentSidebarCollapsed } = useContext(LayoutContext);

  const flattenSidebarData = useMemo(() => {
    return sidebarData.flatMap(
      (item) =>
        item.attributes.document_pages.data?.map((page) => {
          const breadcrumbs = [
            t('document', { ns: 'document' }),
            capitalize(productId?.[0]?.replaceAll('-', ' ')),
          ];

          breadcrumbs.push(item.attributes.name);
          breadcrumbs.push(page.attributes.title);

          return {
            title: page.attributes.title,
            slug: page.attributes.slug,
            parentSlug: item.attributes.slug,
            breadcrumbs,
          };
        }) ?? []
    );
  }, [sidebarData, productId, t]);

  const previousPage = useMemo(() => {
    const parentSlug = productId?.[1];
    const slug = productId?.[2];
    const currentPageIndex = flattenSidebarData?.findIndex(
      (item) => item.slug === slug && item.parentSlug === parentSlug
    );
    if (currentPageIndex === 0) {
      return null;
    }
    return flattenSidebarData?.[currentPageIndex - 1];
  }, [flattenSidebarData, productId]);

  const nextPage = useMemo(() => {
    const parentSlug = productId?.[1];
    const slug = productId?.[2];
    const currentPageIndex = flattenSidebarData?.findIndex(
      (item) => item.slug === slug && item.parentSlug === parentSlug
    );
    if (currentPageIndex === flattenSidebarData?.length - 1) {
      return null;
    }
    return flattenSidebarData?.[currentPageIndex + 1];
  }, [flattenSidebarData, productId]);

  const breadcrumbData = useMemo(() => {
    const listData = [];
    const indexParentDocumentSidebarSlug = 1;
    const indexChildDocumentSidebarSlug = 2;
    sidebarData?.forEach((item) => {
      if (item?.attributes?.slug === productId?.[indexParentDocumentSidebarSlug]) {
        listData.push({
          label: item?.attributes?.name,
          href: `/document/${productId?.[0]}/${item?.attributes?.slug}`,
        });
        if (productId?.length > 2) {
          item?.attributes?.document_pages?.data?.forEach((pageDocument) => {
            if (pageDocument?.attributes?.slug === productId?.[indexChildDocumentSidebarSlug]) {
              listData.push({
                label: pageDocument?.attributes?.title,
                // eslint-disable-next-line max-len
                href: `/document/${productId?.[0]}/${item?.attributes?.slug}/${pageDocument?.attributes?.slug}`,
              });
            }
          });
        }
      }
    });
    return listData;
  }, [sidebarData, productId]);

  const baseDocumentBreadcrumbs = useMemo(() => {
    return [
      {
        label: t('document', { ns: 'document' }),
        href: `/document`,
      },
      {
        label: capitalize(productId?.[0]?.replaceAll('-', ' ')),
        href: `/document/${productId?.[0]}`,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // refresh contents
  const handleFetchData = async () => {
    try {
      if (!isDocumentHomePage && productId?.[2]) {
        const newDocumentData = await getPageDocument({
          featureSlug: productId?.[2],
          locale: i18n?.language,
          productSlug: productId?.[0],
        });
        setDocumentData(newDocumentData);
      } else {
        const newHomePagesData = await getDocumentHomePages({
          productSlug: productId?.[0],
          documentSlug: productId?.[1],
          locale: i18n?.language,
        });
        setDocumentHomePageData(newHomePagesData);
        setDocumentData(newHomePagesData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('handleFetchData - error:', error);
    }
  };

  const handleCopy = async (action = '') => {
    if (action === COPY_ACTIONS.COPY_AS_MD) {
      setCopying(true);
      setTimeout(() => {
        setCopying(false);
      }, 5000);
      const result = turndownService.turndown(documentData?.[0]?.attributes?.content);
      try {
        await navigator.clipboard.writeText(result);
      } catch (error) {
        console.error('Copy thất bại:', error);
      }
      return;
    }
    if (action === COPY_ACTIONS.OPEN_IN_CHATGPT) {
      window.open(`https://chatgpt.com/?q=Read ${window.location.href}`, '_blank');
    }
    if (action === COPY_ACTIONS.OPEN_IN_CLAUDE) {
      window.open(`https://claude.ai/new?q=Read ${window.location.href}`, '_blank');
    }
    setCopyVisible(false);
  };

  const handleCloseZoomImage = () => {
    setImageSrc(null);
  };

  useEffect(() => {
    handleFetchData();
    const timer = setTimeout(() => {
      scrollToTop();
    }, 200);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language, productId]);

  useEffect(() => {
    const contentText = currentContent.current?.textContent?.trim() || '';
    const wordCount = contentText ? contentText.split(/\s+/).length : 0;
    setReadingMinutes(Math.max(1, Math.ceil(wordCount / 200)));

    const elements = document.querySelectorAll(
      '.ck-content-document h1, .ck-content-document h2, .ck-content-document h3'
    );
    if (elements) {
      const menuTitle = [];
      elements.forEach((element, index) => {
        if (element && element.textContent) {
          const titleSection = element.textContent.trim();
          const id = `document-${index}`;
          menuTitle.push({ title: titleSection, id });
          element.setAttribute('id', id);
        }
      });
      setHighLightTitle(menuTitle);
    }
  }, [documentData]);

  useEffect(() => {
    let rafId = null;

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        const scrollTop = window.scrollY;
        const shouldBeSticky = scrollTop > 70 && isProductDetailPage && isDesktop;

        setStickyMode(shouldBeSticky);
        const footerRef = document.querySelector('#document-footer');
        const footerTop = footerRef.getBoundingClientRect().top;

        const windowHeight = window.innerHeight;
        const offset = Math.max(0, windowHeight - footerTop);

        tocRef.current?.style.setProperty('--toc-offset', `${offset}px`);
      });
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isDesktop, isProductDetailPage]);

  useEffect(() => {
    const handleResize = () => {
      right.current = tocRef.current?.getBoundingClientRect().width;
    };
    setTimeout(() => handleResize(), 0);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [router.asPath]);

  useEffect(() => {
    if (isProductDetailPage) {
      setTimeout(() => {
        const zoomBtns = document.querySelectorAll('button[type="button"]');

        zoomBtns.forEach((zoomBtn) => {
          zoomBtn.addEventListener('click', async () => {
            const img = zoomBtn.closest('.image-container')?.querySelector('img');

            if (!img) return;

            const imageUrl = img.src;
            if (imageUrl) {
              setImageSrc(imageUrl);
            }
          });
        });
      }, 500);
    }
  }, [productId]);

  useEffect(() => {
    if (!isProductDetailPage) return;

    const timeout = setTimeout(() => {
      const copyButtons = document.querySelectorAll('.copy-btn');

      const onCopy = async (event) => {
        const button = event.currentTarget;
        const code = button.closest('#code-template')?.querySelector('pre code');

        if (!code) return;

        const codeText = code.textContent;

        await navigator.clipboard.writeText(codeText);

        toast.success(t('copied', { ns: 'document' }));
      };

      copyButtons.forEach((button) => {
        button.addEventListener('click', onCopy);
      });

      // Cleanup event listener
      return () => {
        copyButtons.forEach((button) => {
          button.removeEventListener('click', onCopy);
        });
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [productId, isProductDetailPage]);

  const title = isProductIntroPage
    ? initProductData?.[0]?.attributes?.name
    : isDocumentHomePage
    ? `${productName}`
    : documentData?.[0]?.attributes?.title;

  return (
    <Page
      title={`${title ? `${title} |` : ''} CMC Cloud`}
      metaDescription={t('meta_new_detail', { ns: 'news' })}
      px={0}
      sx={{ height: '100%' }}
    >
      <Dialog
        open={!!zoomingImageSrc}
        onClose={handleCloseZoomImage}
        maxWidth={false}
        fullWidth
        scroll="paper"
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
        PaperProps={{
          sx: {
            width: 'calc(100% - 64px)',
            maxHeight: 'calc(100% - 64px)',
            m: '32px',
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleCloseZoomImage}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1400,
            width: 40,
            height: 40,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.4)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.6)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ width: '100%' }}>
          <Image
            src={zoomingImageSrc || ''}
            alt="zooming-img"
            width={1200}
            height={800}
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        </Box>
      </Dialog>

      {!isProductIntroPage && (
        <Grid
          sx={{
            backgroundColor: '#f1f3f5',
          }}
          container
          columnSpacing={{ xs: 1, md: 2.5 }}
          rowSpacing={{ xs: 1.5, md: 2 }}
        >
          <Grid
            item
            xs={12}
            md={isTocOpen ? 9 : 11.7}
            sx={{
              marginRight: stickyMode && isTocOpen ? right.current / 10 : 'unset',
            }}
          >
            {!isEmpty(documentData) && (
              <Box
                sx={{
                  m: { xs: 0, md: 2.5 },
                  minHeight: 'calc(100vh - 70px)',
                  p: { xs: 2, md: 3 },
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(24, 39, 48, 0.08)',
                }}
              >
                <CMCCloudBreadcrumbs
                  items={[...baseDocumentBreadcrumbs, ...breadcrumbData]}
                  sidebarData={sidebarData}
                  sx={{ pl: 0, mb: 1.5 }}
                />
                <Box>
                  <Typography
                    fontSize={{ xs: 22, md: 28 }}
                    fontWeight={600}
                    sx={{
                      lineHeight: 1.3,
                      mb: 1,
                      mt: 2,
                      color: '#231F20',
                      fontWeight: 600,
                      fontSize: 32,
                    }}
                    color={theme.palette.text.default}
                    variant="h2"
                  >
                    {documentData?.[0]?.attributes?.title}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                      borderBottom: '1px solid #E6E6E6',
                      pb: 1.25,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        color: '#403B3C',
                      }}
                    >
                      <Typography
                        fontSize="14px"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 400,
                          color: '#5E5E5E',
                        }}
                      >
                        <AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />
                        {router.locale === 'vi'
                          ? `Đọc trong ${readingMinutes} phút`
                          : `${readingMinutes} min read`}
                      </Typography>
                      <span style={{ fontSize: '12px', color: '#D1D1D1' }}>|</span>
                      <Typography
                        fontSize="14px"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 400,
                          color: '#5E5E5E',
                        }}
                      >
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
                        {t('last_updated', {
                          time: moment(documentData?.[0]?.attributes?.updatedAt).format(
                            DATE_TIME_FORMAT
                          ),
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          p: 0,
                          background: 'transparent',
                          fontWeight: 500,
                          font: 'inherit',
                          fontSize: 14,
                          cursor: 'pointer',
                          color: '#5E5E5E',
                          border: 'solid 1px #d1d1d1',
                          borderRadius: '8px',
                        }}
                      >
                        <Box
                          component="button"
                          disabled={copying}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 8px',
                            borderRight: '1px solid #d1d1d1',
                          }}
                          onClick={() => handleCopy(COPY_ACTIONS.COPY_AS_MD)}
                        >
                          <Image
                            style={{ marginRight: '4px', marginTop: '4px' }}
                            width={20}
                            height={20}
                            src={`/images/${copying ? 'copied' : 'copy'}.svg`}
                            alt="markdown"
                          />
                          {t(copying ? 'copied' : 'copy', { ns: 'document' })}
                        </Box>
                        <Box
                          ref={copyBtnRef}
                          onClick={() => setCopyVisible(true)}
                          component="span"
                          sx={{ padding: '6px 8px 6px 0px' }}
                        >
                          <Image
                            src="/images/chevron-down.svg"
                            alt="arrow-down"
                            width={24}
                            height={24}
                          />
                        </Box>
                      </Box>
                      <Popover
                        id="copy-document"
                        open={copyVisible}
                        anchorEl={copyBtnRef.current}
                        onClose={() => setCopyVisible(false)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        disableScrollLock
                        PaperProps={{
                          sx: {
                            boxShadow: '0px 24px 40px -12px #231F202E',
                            marginTop: '8px',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%', maxWidth: 360 }}>
                          <List>
                            <ListItem
                              disabled={copying}
                              onClick={() => handleCopy(COPY_ACTIONS.COPY_AS_MD)}
                            >
                              <ListItemButton>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Image
                                    width={20}
                                    height={20}
                                    src={`/images/${copying ? 'copied' : 'copy'}.svg`}
                                    alt="markdown"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#231F20',
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#5E5E5E',
                                  }}
                                  primary={t('copy_as_markdown', { ns: 'document' })}
                                  secondary={t('copy_page_as_markdown', { ns: 'document' })}
                                />
                              </ListItemButton>
                            </ListItem>
                            <ListItem>
                              <ListItemButton
                                component="a"
                                href={`/document/markdown/?productSlug=${productId[0]}&featureSlug=${productId[2]}`}
                                target="_blank"
                              >
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Image
                                    width={20}
                                    height={20}
                                    src="/images/markdown.svg"
                                    alt="markdown"
                                  />
                                </ListItemIcon>

                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#231F20',
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#5E5E5E',
                                  }}
                                  primary={t('view_as_markdown', { ns: 'document' })}
                                  secondary={t('view_page_as_markdown', { ns: 'document' })}
                                />
                              </ListItemButton>
                            </ListItem>
                          </List>
                          <Divider sx={{ borderColor: '#F2F2F2' }} />
                          <List>
                            <ListItem onClick={() => handleCopy(COPY_ACTIONS.OPEN_IN_CHATGPT)}>
                              <ListItemButton>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Image
                                    width={20}
                                    height={20}
                                    src="/images/chatgpt.svg"
                                    alt="chatgpt"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#231F20',
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#5E5E5E',
                                  }}
                                  primary={t('open_in_chatgpt', { ns: 'document' })}
                                  secondary={t('ask_chatgpt_about_this_page', { ns: 'document' })}
                                />
                              </ListItemButton>
                            </ListItem>
                            <ListItem onClick={() => handleCopy(COPY_ACTIONS.OPEN_IN_CLAUDE)}>
                              <ListItemButton>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Image
                                    width={20}
                                    height={20}
                                    src="/images/claude.svg"
                                    alt="claude"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#231F20',
                                  }}
                                  secondaryTypographyProps={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#5E5E5E',
                                  }}
                                  primary={t('open_in_claude', { ns: 'document' })}
                                  secondary={t('ask_claude_about_this_page', { ns: 'document' })}
                                />
                              </ListItemButton>
                            </ListItem>
                          </List>
                        </Box>
                      </Popover>
                    </Box>
                  </Box>
                </Box>
                <Box
                  ref={currentContent}
                  className="ck-content ck-content-document"
                  dangerouslySetInnerHTML={{
                    __html: documentData?.[0]?.attributes?.content,
                  }}
                  sx={{
                    color: '#231F20',
                  }}
                  py={2}
                />
                <Box>
                  <Divider
                    sx={{
                      border: 'none',
                      height: '1px',
                      backgroundImage:
                        'repeating-linear-gradient(to right, #D1D1D1 0px, #D1D1D1 6px, transparent 6px, transparent 18px)',
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Link
                      href={`/document/${productId[0]}/${previousPage?.parentSlug}/${previousPage?.slug}`}
                      style={{
                        borderRadius: '8px',
                        transition: 'all .3s',
                        visibility: previousPage ? 'visible' : 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.border = 'solid 1px #E6E6E6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.border = 'solid 1px transparent';
                      }}
                    >
                      <Box
                        component=""
                        sx={{
                          cursor: 'pointer',
                          padding: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Image
                          src="/images/chevron-left.svg"
                          alt="previous_page"
                          width={16}
                          height={16}
                        />
                        <Box sx={{ ml: 1.5 }}>
                          <Typography sx={{ color: '#5E5E5E', fontWeight: 400, fontSize: '12px' }}>
                            {t('previous_page', { ns: 'document' })}
                          </Typography>
                          <Typography fontSize={14}>{previousPage?.title}</Typography>
                        </Box>
                      </Box>
                    </Link>
                    <Link
                      href={`/document/${productId[0]}/${nextPage?.parentSlug}/${nextPage?.slug}`}
                      style={{
                        borderRadius: '8px',
                        transition: 'all .3s',
                        visibility: nextPage ? 'visible' : 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.border = 'solid 1px #E6E6E6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.border = 'solid 1px transparent';
                      }}
                    >
                      <Box
                        sx={{
                          cursor: 'pointer',
                          padding: '12px 12px 0px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ mr: 1.5 }}>
                          <Typography
                            sx={{
                              color: '#5E5E5E',
                              fontWeight: 400,
                              fontSize: '12px',
                              textAlign: 'right',
                            }}
                          >
                            {t('next_page', { ns: 'document' })}
                          </Typography>
                          <Typography fontSize={14}>{nextPage?.title}</Typography>
                        </Box>
                        <Image
                          src="/images/chevron-right-1.svg"
                          alt="next_page"
                          width={16}
                          height={16}
                        />
                      </Box>
                    </Link>
                  </Box>
                </Box>
              </Box>
            )}
          </Grid>
          {/* table content */}
          <Grid
            item
            xs={12}
            md={isTocOpen ? 3 : 0.3}
            sx={{
              backgroundColor: isTocOpen ? '#fff' : 'transparent',
              pl: '0 !important',
              position: stickyMode ? 'fixed' : 'relative',
              top: 0,
              right: 0,
              width: stickyMode && isDocumentSidebarCollapsed ? '100%' : right.current,
              zIndex: stickyMode ? 197 : 'unset',
              height: stickyMode ? `calc(100vh - var(--toc-offset, 0px))` : 'auto',
            }}
            ref={tocRef}
          >
            <Box
              sx={{
                minHeight: 'calc(100vh - 120px)',
                p: isTocOpen ? 2 : 0,
                pt: 2,
              }}
            >
              {productId.length >= 3 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    {isTocOpen && (
                      <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#231F20' }}>
                        {t('on_this_page', { ns: 'document' })}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      aria-label={isTocOpen ? 'Ẩn mục lục' : 'Mở mục lục'}
                      onClick={() => setIsTocOpen((open) => !open)}
                    >
                      {isTocOpen ? (
                        <Image
                          width={20}
                          height={20}
                          src="/images/chevron-right-double.svg"
                          alt="open-toc"
                        />
                      ) : (
                        <Image
                          width={20}
                          height={20}
                          src="/images/chevron-left-double.svg"
                          alt="close-toc"
                        />
                      )}
                    </IconButton>
                  </Box>
                  {isTocOpen &&
                    documentData?.map((section) => (
                      <Box key={section.id}>
                        <MenuDetailDocument sections={highlightTitle} />
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
      {/* Document home page */}
      {isProductIntroPage && !isEmpty(sidebarData) && (
        <DocumentHomePage sidebarData={sidebarData} productData={initProductData} />
      )}
      {/* No content */}
      {((isDocumentHomePage && isEmpty(documentHomePage)) ||
        (isEmpty(documentData) && !isDocumentHomePage && !isProductIntroPage) ||
        (isProductIntroPage && isEmpty(sidebarData))) && <NoContent />}
    </Page>
  );
}
